<script setup lang="ts">
/**
 * The signed-in person at the foot of the sidebar, opening onto their menu:
 * notifications (their own choice of optional messages), language, and sign out.
 *
 * Sign out is offered for browser sessions only. Inside the Lark client
 * requestAccess() signs the person straight back in on the next load, so the
 * entry would appear to do nothing — the rule the old header followed.
 */
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { storeToRefs } from 'pinia'
import { Bell, Check, ChevronsUpDown, Globe, LogOut, Shield } from 'lucide-vue-next'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useDashboardStore } from '@/stores/dashboard'
import { getSupportedLocales, setLocale, type SupportedLocale } from '@/locales/i18n'

const props = defineProps<{
  collapsed: boolean
  showTip: (text: string, event: Event) => void
  hideTip: () => void
}>()

const { t, locale } = useI18n()
const auth = useAuthStore()
const { isAppAdmin } = storeToRefs(useDashboardStore())

const open = ref(false)
const rootRef = ref<HTMLElement | null>(null)
const buttonRef = ref<HTMLButtonElement | null>(null)
const panelRef = ref<HTMLElement | null>(null)

const name = computed(() => auth.user?.name ?? '')
const initials = computed(() => {
  const parts = (auth.user?.name || '').split(' ').filter(Boolean)
  return parts.map(p => p[0]).join('').toUpperCase().slice(0, 2) || '?'
})
const locales = getSupportedLocales()

/** "Signed in with Microsoft" — which sign-in this is, when the person could have another. */
const signedInWith = computed(() => {
  if ((auth.providers?.platforms.length ?? 1) < 2) return ''
  switch (auth.platform) {
    case 'microsoft': return t('signedInWith', { provider: t('platformMicrosoft') })
    case 'google': return t('signedInWith', { provider: t('platformGoogle') })
    case 'email': return t('signedInWith', { provider: t('platformEmail') })
    default: return t('signedInWith', { provider: t('platformLark') })
  }
})
const currentLocale = computed(() => locale.value as SupportedLocale)

function toggle(): void {
  open.value = !open.value
  props.hideTip()
  if (open.value) void nextTick(() => panelRef.value?.querySelector<HTMLElement>('button, a')?.focus())
}

function close(focusButton = false): void {
  open.value = false
  if (focusButton) void nextTick(() => buttonRef.value?.focus())
}

function choose(code: SupportedLocale): void {
  setLocale(code)
}

const router = useRouter()

/** The person's own choice of optional messages (contact center). */
function openNotifications(): void {
  close()
  void router.push('/notifications')
}

function signOut(): void {
  close()
  auth.logout()
}

/** Swallowed only while open, so Escape still closes the drawer around it. */
function onEscape(event: KeyboardEvent): void {
  if (!open.value) return
  event.stopPropagation()
  close(true)
}

function onDocumentClick(event: MouseEvent): void {
  if (open.value && rootRef.value && !rootRef.value.contains(event.target as Node)) close()
}

onMounted(() => document.addEventListener('click', onDocumentClick))
onBeforeUnmount(() => document.removeEventListener('click', onDocumentClick))
</script>

<template>
  <div ref="rootRef" class="relative" @keydown.esc="onEscape">
    <button
      ref="buttonRef"
      type="button"
      :aria-expanded="open"
      aria-controls="shell-user-menu"
      :aria-label="collapsed ? t('userMenu') : undefined"
      :class="[
        'flex w-full items-center gap-2.5 rounded-lg p-1.5 text-left outline-none hover:bg-gray-100 focus-visible:ring-2 focus-visible:ring-blue-500/70 dark:hover:bg-white/5 motion-safe:transition-colors',
        collapsed ? 'justify-center' : '',
        open ? 'bg-gray-100 dark:bg-white/5' : '',
      ]"
      @click.stop="toggle"
      @mouseenter="showTip(name, $event)"
      @mouseleave="hideTip"
      @focus="showTip(name, $event)"
      @blur="hideTip"
    >
      <span class="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-blue-100 text-xs font-semibold text-blue-700 dark:bg-blue-500/20 dark:text-blue-200">
        <img v-if="auth.user?.avatarUrl" :src="auth.user.avatarUrl" alt="" class="h-full w-full object-cover" />
        <span v-else aria-hidden="true">{{ initials }}</span>
      </span>
      <template v-if="!collapsed">
        <span class="min-w-0 flex-1 leading-tight">
          <span class="block truncate text-sm font-medium text-gray-900 dark:text-gray-100">{{ name }}</span>
          <span v-if="isAppAdmin" class="block truncate text-xs text-gray-500 dark:text-gray-400">{{ t('appAdmin') }}</span>
        </span>
        <ChevronsUpDown :size="15" class="shrink-0 text-gray-400" aria-hidden="true" />
      </template>
    </button>

    <Transition
      enter-active-class="motion-safe:transition motion-safe:duration-150 motion-safe:ease-out"
      leave-active-class="motion-safe:transition motion-safe:duration-100 motion-safe:ease-in"
      enter-from-class="opacity-0 translate-y-1"
      leave-to-class="opacity-0 translate-y-1"
    >
      <div
        v-if="open"
        id="shell-user-menu"
        ref="panelRef"
        :class="[
          'absolute z-[60] w-64 rounded-xl border border-gray-200 bg-white p-1.5 shadow-xl dark:border-white/10 dark:bg-gray-900',
          collapsed ? 'bottom-0 left-full ml-3' : 'bottom-full left-0 mb-2',
        ]"
      >
        <div class="flex items-center gap-2.5 px-2.5 py-2">
          <div class="min-w-0 flex-1">
            <div class="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{{ name }}</div>
            <div v-if="auth.user?.tenantName" class="truncate text-xs text-gray-500 dark:text-gray-400">{{ auth.user.tenantName }}</div>
            <!-- Only once there is more than one way in: a Lark-only portal says nothing new. -->
            <div v-if="signedInWith" class="truncate text-xs text-gray-400 dark:text-gray-500">{{ signedInWith }}</div>
          </div>
          <span
            v-if="isAppAdmin"
            class="inline-flex shrink-0 items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800 dark:bg-amber-500/15 dark:text-amber-300"
          >
            <Shield :size="11" aria-hidden="true" />
            {{ t('appAdmin') }}
          </span>
        </div>

        <div class="my-1 h-px bg-gray-100 dark:bg-white/10" />

        <button
          type="button"
          class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-sm text-gray-700 outline-none hover:bg-gray-100 focus-visible:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/5 dark:focus-visible:bg-white/5"
          @click="openNotifications"
        >
          <Bell :size="15" class="text-gray-400" aria-hidden="true" />
          {{ t('notificationsMenu') }}
        </button>

        <div class="my-1 h-px bg-gray-100 dark:bg-white/10" />

        <div role="group" :aria-labelledby="'shell-language-label'">
          <div id="shell-language-label" class="flex items-center gap-2 px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
            <Globe :size="12" aria-hidden="true" />
            {{ t('language') }}
          </div>
          <button
            v-for="option in locales"
            :key="option.code"
            type="button"
            :aria-pressed="option.code === currentLocale"
            :lang="option.code"
            class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-sm text-gray-700 outline-none hover:bg-gray-100 focus-visible:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/5 dark:focus-visible:bg-white/5"
            @click="choose(option.code)"
          >
            <span class="w-5 text-center" aria-hidden="true">{{ option.flag }}</span>
            <span class="flex-1">{{ option.name }}</span>
            <Check v-if="option.code === currentLocale" :size="15" class="text-blue-600 dark:text-blue-400" aria-hidden="true" />
          </button>
        </div>

        <template v-if="auth.isBrowserSession">
          <div class="my-1 h-px bg-gray-100 dark:bg-white/10" />
          <button
            type="button"
            class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left text-sm text-gray-700 outline-none hover:bg-gray-100 focus-visible:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/5 dark:focus-visible:bg-white/5"
            @click="signOut"
          >
            <LogOut :size="15" class="text-gray-400" aria-hidden="true" />
            {{ t('logout') }}
          </button>
        </template>
      </div>
    </Transition>
  </div>
</template>
